import AntDateRangeChipsPicker from '@/shared/Inputs/FilterChips/AntDateRangeChipsPicker'
import CaretRightOutlinedIcon from '@/assets/icons/caret-right-outlined.svg?react'
import PauseOutlinedIcon from '@/assets/icons/pause-outlined.svg?react'
import RedoOutlinedIcon from '@/assets/icons/redo-outlined.svg?react'
import DropdownInput from '@/shared/Inputs/DropdownInput'
import playerWidgetConstants from './playerWidget.constants'
import css from './playerWidget.module.css'
import timeUtils from '@/utils/time.utils'
import PlayerWidgetTimeLine from './components/PlayerWidgetTimeLine'
import usePlayerWidget from '../usePlayerWidget'
import playerWidgetUtils from './playerWidget.utils'
import useTimer from '@/shared/hooks/useTimer'
import { memo, useEffect, useRef } from 'react'
import CircleLoader from '@/shared/Loaders/CircleLoader'

interface IProps {
  ctrl: ReturnType<typeof usePlayerWidget>
}

const PlayerWidget = (props: IProps) => {
  const timeCtrl = useTimer(playerWidgetConstants.autoStartDelaySec)
  const pendingStartRef = useRef(false)
  const controlIconStyle = { width: 20, height: 20 } as const

  const renderPlayBtnIcon = () => {
    if (timeCtrl.isRunning) return <CircleLoader style={{ padding: '2px', height: '20px', width: '20px' }} />
    if (props.ctrl.tsCurrent === props.ctrl.tsTo) return <RedoOutlinedIcon style={{ ...controlIconStyle, height: 18 }} />
    if (!props.ctrl.isPlaying) return <CaretRightOutlinedIcon style={{ ...controlIconStyle, transform: 'translateX(1px)' }} />
    return <PauseOutlinedIcon style={controlIconStyle} />
  }

  useEffect(() => {
    if (pendingStartRef.current && timeCtrl.remainingTime === 0) {
      pendingStartRef.current = false
      props.ctrl.start()
    }
  }, [timeCtrl.time, props.ctrl.start])

  useEffect(() => {
    return () => {
      props.ctrl.stop()
      props.ctrl.updateTimeInterval(null, null)
    }
  }, [])

  const handleTimeIntervalChange = (tsFrom: number, tsTo: number) => {
    if (!tsFrom || !tsTo) return
    if (Math.abs(timeUtils.differenceInMinutes(tsTo, tsFrom)) > 10) {
      props.ctrl.updateTimeInterval(null, null)
      return
    }
    if (timeUtils.isAfter(tsFrom, tsTo)) [tsFrom, tsTo] = [tsTo, tsFrom] // TODO: maybe return
    if (timeUtils.isAfter(tsTo, new Date())) return
    const prevDates = [props.ctrl.tsFrom, props.ctrl.tsTo]
    props.ctrl.updateTimeInterval(tsFrom, tsTo)
    if (!prevDates[0] && !prevDates[1]) {
      pendingStartRef.current = true
      timeCtrl.start(true)
    }
  }

  const renderPlayer = () => {
    if (props.ctrl.tsFrom == null || props.ctrl.tsTo == null) return null
    return (
      <div className={css.playerContainer}>
        <div className={css.timeLineContainer}>
          <div className={css.timelineBoundaries}>
            <span className="text-sm text-secondary">НАЧАЛО</span>
            <span className="text-sm">{timeUtils.formatDate(props.ctrl.tsFrom, 'dd MMM, HH:mm:ss')}</span>
          </div>
          <PlayerWidgetTimeLine
            forceRerender={props.ctrl.forceUpdate}
            tsCurrent={props.ctrl.tsCurrent}
            tsFrom={props.ctrl.tsFrom}
            tsTo={props.ctrl.tsTo}
            onSeekTo={props.ctrl.seekTo}
            onMouseDown={props.ctrl.stop}
          />
          <div className={css.timelineBoundaries}>
            <span className="text-sm text-secondary">ТЕКУЩЕЕ</span>
            <span className="text-sm">{timeUtils.formatDate(props.ctrl.tsCurrent, 'dd MMM, HH:mm:ss')}</span>
          </div>
        </div>
        <div className={css.speedInputContainer}>
          <DropdownInput
            type="inline"
            menuStyle={{ width: '105px' }}
            wrapperStyle={{ width: '56px', height: '40px', justifyContent: 'space-between', gap: 0 }}
            options={playerWidgetConstants.speedTypeOptions}
            selectedOption={props.ctrl.playbackSpeed}
            title=""
            onSelect={props.ctrl.changePlaybackSpeed}
            name="lineMode"
          />
        </div>
        <div
          className={css.startStopBtn}
          onClick={() => {
            if (!timeCtrl.isRunning) props.ctrl.handleChangePlay()
          }}
        >
          <span className={css.startStopBtnIcon}>{renderPlayBtnIcon()}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={css.container} style={{ flex: !props.ctrl.tsFrom || !props.ctrl.tsTo ? '0' : '1 1 0' }}>
      {renderPlayer()}
      <div className={css.rangePickerContainer}>
        <AntDateRangeChipsPicker
          disabledDate={(date: number, _type: string, range: 'start' | 'end', from: number) => {
            if (range === 'start' || !from) return timeUtils.isAfter(date, new Date())
            return timeUtils.isAfter(date, timeUtils.addMinutes(from, 10)) || timeUtils.isBefore(date, from)
          }}
          disabledRangeTime={(_, range, from) => playerWidgetUtils.disabledRangeTime(range, from)}
          type="inline"
          startTs={props.ctrl.tsFrom}
          endTs={props.ctrl.tsTo}
          onChange={handleTimeIntervalChange}
          allowSelectInFuture={false}
          style={{ minWidth: props.ctrl.tsFrom && props.ctrl.tsTo ? '75px' : '142px' }}
          title={props.ctrl.tsFrom && props.ctrl.tsTo ? 'Период' : 'Выберите пероид'}
          isTipHidden
          withTime
          isCloseHidden
          onOpen={() => {
            pendingStartRef.current = false
            timeCtrl.stop()
            props.ctrl.stop()
          }}
        />
      </div>
    </div>
  )
}

export default memo(PlayerWidget)
